import { useState, useEffect } from 'react';
import { useWallet } from './useWallet';
import { getSOLBalance } from '../utils/blockchainUtils';
import { UI_CONFIG } from '../config/constants';

/**
 * useBalance Hook - Real-time SOL balance tracking
 *
 * Automatically fetches and updates the user's SOL balance:
 * - Fetches balance when wallet connects
 * - Auto-refreshes at regular intervals
 * - Handles loading and error states
 * - Provides manual refresh function
 *
 * This hook is independent and can be easily modified or disabled.
 */
export function useBalance() {
    const { publicKey, connected } = useWallet();
    const [balance, setBalance] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Fetch balance from blockchain
     */
    const fetchBalance = async () => {
        if (!publicKey || !connected) {
            setBalance(0);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const bal = await getSOLBalance(publicKey);
            setBalance(bal);
        } catch (err) {
            console.error('Error fetching balance:', err);
            setError(err.message || 'Failed to fetch balance');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Manual refresh function
     */
    const refresh = () => {
        fetchBalance();
    };

    // Fetch balance when wallet connects
    useEffect(() => {
        if (connected && publicKey) {
            fetchBalance();
        } else {
            setBalance(0);
        }
    }, [connected, publicKey]);

    // Auto-refresh balance at regular intervals
    useEffect(() => {
        if (!connected || !publicKey) {
            return;
        }

        const interval = setInterval(() => {
            fetchBalance();
        }, UI_CONFIG.BALANCE_REFRESH_INTERVAL);

        return () => clearInterval(interval);
    }, [connected, publicKey]);

    return {
        balance,
        loading,
        error,
        refresh,
    };
}
